<template>
    <div class="skeleton-loader" :class="`skeleton-loader--${type}`">
        <template v-if="type === 'agenda-cards'">
            <div v-for="n in items" :key="n" class="sk-card">
                <div class="sk-bone sk-card__thumb" />
                <div class="sk-card__lines">
                    <span class="sk-bone sk-line sk-line--lg" />
                    <span class="sk-bone sk-line sk-line--md" />
                    <span class="sk-bone sk-line sk-line--sm" />
                </div>
            </div>
        </template>

        <template v-else-if="type === 'week-dots'">
            <div v-for="n in items" :key="n" class="sk-day">
                <span class="sk-bone sk-day__label" />
                <span class="sk-bone sk-day__dot" />
            </div>
        </template>

        <template v-else-if="type === 'exercise-history'">
            <div v-for="n in items" :key="n" class="sk-hist-row">
                <span class="sk-bone sk-hist-date" />
                <div class="sk-hist-data">
                    <span class="sk-bone sk-line sk-line--md" />
                    <span class="sk-bone sk-line sk-line--sm" />
                </div>
            </div>
        </template>

        <template v-else-if="type === 'chat-list'">
            <div v-for="n in items" :key="n" class="sk-chat-card">
                <div class="sk-bone sk-chat-thumb" />
                <div class="sk-chat-lines">
                    <span class="sk-bone sk-line sk-line--md" />
                    <span class="sk-bone sk-line sk-line--lg" />
                    <span class="sk-bone sk-line sk-line--sm" />
                </div>
            </div>
        </template>

        <template v-else-if="type === 'chat'">
            <div v-for="n in items" :key="n" class="sk-msg" :class="n % 2 === 0 ? 'sk-msg--me' : 'sk-msg--them'">
                <span class="sk-bone sk-bubble" :style="{ width: bubbleWidths[(n - 1) % bubbleWidths.length] }" />
            </div>
        </template>

        <template v-else-if="type === 'circles'">
            <span v-for="n in items" :key="n" class="sk-bone sk-circle" />
        </template>

        <template v-else-if="type === 'team-bars'">
            <div v-for="n in items" :key="n" class="sk-tb-row">
                <span class="sk-bone sk-tb-name" />
                <span class="sk-bone sk-tb-bar" />
            </div>
        </template>

        <template v-else-if="type === 'team-cards'">
            <div v-for="n in items" :key="n" class="sk-tc-card">
                <span class="sk-bone sk-tc-dot" />
                <span class="sk-bone sk-tc-name" />
                <span class="sk-bone sk-tc-meta" />
                <span class="sk-bone sk-tc-chev" />
            </div>
        </template>

        <template v-else-if="type === 'stat-grid'">
            <span class="sk-bone sk-sg-card" v-for="n in items" :key="n" />
        </template>

        <template v-else-if="type === 'stat-table'">
            <span class="sk-bone sk-stt-head" />
            <span class="sk-bone sk-stt-row" v-for="n in items" :key="n" />
        </template>

        <template v-else-if="type === 'eval-stats'">
            <div class="sk-es-top">
                <span class="sk-bone sk-es-card" />
                <span class="sk-bone sk-es-table" />
            </div>
            <div class="sk-es-charts">
                <span class="sk-bone sk-es-chart" v-for="n in 3" :key="n" />
            </div>
        </template>

        <template v-else-if="type === 'table'">
            <div class="sk-tbl-head" />
            <div v-for="n in items" :key="n" class="sk-tbl-row">
                <span class="sk-bone sk-tbl-avatar" />
                <div class="sk-tbl-lines">
                    <span class="sk-bone sk-line sk-line--md" />
                    <span class="sk-bone sk-line sk-line--sm" />
                </div>
                <span class="sk-bone sk-tbl-cell" />
                <span class="sk-bone sk-tbl-cell sk-tbl-cell--sm" />
            </div>
        </template>

        <template v-else-if="type === 'rows'">
            <div v-for="n in items" :key="n" class="sk-row">
                <span class="sk-bone sk-line sk-line--lg" />
                <span class="sk-bone sk-row__ctrl" />
            </div>
        </template>

        <template v-else-if="type === 'lib-rows'">
            <div v-for="n in items" :key="n" class="sk-lib-row">
                <span class="sk-bone sk-lib-icon" />
                <div class="sk-lib-lines">
                    <span class="sk-bone sk-line sk-line--lg" />
                    <span class="sk-bone sk-line sk-line--sm" />
                </div>
                <span class="sk-bone sk-lib-more" />
            </div>
        </template>

        <template v-else-if="type === 'zapping-select'">
            <span class="sk-bone sk-zap-desc" />
            <span class="sk-bone sk-zap-desc sk-zap-desc--short" />
            <div class="sk-zap-tabs">
                <span class="sk-bone sk-zap-tab" />
                <span class="sk-bone sk-zap-tab" />
                <span class="sk-bone sk-zap-tab" />
            </div>
            <span class="sk-bone sk-zap-field" />
        </template>

        <template v-else-if="type === 'zapping-options'">
            <div class="sk-zap-tabs">
                <span class="sk-bone sk-zap-tab" />
                <span class="sk-bone sk-zap-tab" />
                <span class="sk-bone sk-zap-tab" />
            </div>
            <span class="sk-bone sk-zap-field" />
        </template>

        <template v-else-if="type === 'athlete'">
            <div class="sk-athlete">
                <span class="sk-bone sk-athlete-logo" />
                <span class="sk-bone sk-athlete-name" />
            </div>
        </template>

        <template v-else-if="type === 'assigned-editor'">
            <div class="sk-ae">
                <div class="sk-ae-steps">
                    <span class="sk-bone sk-ae-step" v-for="n in 2" :key="'sp' + n" />
                </div>
                <div class="sk-ae-body">
                    <span class="sk-bone sk-ae-field" v-for="n in 3" :key="'f' + n" />
                    <span class="sk-bone sk-ae-img" />
                </div>
                <div class="sk-ae-actions">
                    <span class="sk-bone sk-ae-btn" v-for="n in 2" :key="'b' + n" />
                </div>
            </div>
        </template>

        <template v-else-if="type === 'assigned-detail'">
            <div class="sk-ad">
                <div class="sk-ad-left">
                    <span class="sk-bone sk-ad-hero" />
                    <div class="sk-ad-stats">
                        <span class="sk-bone sk-ad-stat" v-for="n in 3" :key="'st' + n" />
                    </div>
                </div>
                <div class="sk-ad-right">
                    <span class="sk-bone sk-ad-block" v-for="n in 5" :key="'bl' + n" />
                </div>
            </div>
        </template>

        <template v-else-if="type === 'calendar'">
            <div class="sk-cal-head">
                <span class="sk-bone sk-cal-weekday" v-for="n in 7" :key="'wd' + n" />
            </div>
            <div class="sk-cal-grid">
                <div class="sk-cal-cell" v-for="n in items" :key="'c' + n">
                    <span class="sk-bone sk-cal-date" />
                    <span class="sk-bone sk-cal-ev" v-for="e in (n % 3 === 0 ? 0 : 3)" :key="'e' + e"
                        :style="{ width: (70 + (n * 7 % 25)) + '%' }" />
                </div>
            </div>
        </template>

        <template v-else-if="type === 'calendar-week'">
            <div class="sk-week">
                <div class="sk-week-col" v-for="n in 7" :key="'wc' + n">
                    <div class="sk-week-head">
                        <span class="sk-bone sk-week-weekday" />
                        <span class="sk-bone sk-week-date" />
                    </div>
                    <div class="sk-week-body">
                        <span class="sk-bone sk-week-ev" v-for="e in (n % 4 === 0 ? 1 : 3)" :key="'we' + e" />
                    </div>
                </div>
            </div>
        </template>

        <template v-else-if="type === 'calendar-day'">
            <div class="sk-cday">
                <div class="sk-cday-col sk-cday-col--left">
                    <span class="sk-bone sk-cday-block" v-for="n in 4" :key="'cdb' + n" />
                    <span class="sk-bone sk-cday-save" />
                </div>
                <div class="sk-cday-col sk-cday-col--mid">
                    <span class="sk-bone sk-cday-header" />
                    <span class="sk-bone sk-cday-hist" />
                    <div class="sk-cday-tabs">
                        <span class="sk-bone sk-cday-tab" v-for="n in 4" :key="'cdt' + n" />
                    </div>
                    <span class="sk-bone sk-cday-field" />
                    <span class="sk-bone sk-cday-row" v-for="n in 5" :key="'cdr' + n" />
                </div>
                <div class="sk-cday-col sk-cday-col--right">
                    <span class="sk-bone sk-cday-navbar" />
                    <div class="sk-cday-metrics">
                        <span class="sk-bone sk-cday-metric" v-for="n in 3" :key="'cdm' + n" />
                    </div>
                    <span class="sk-bone sk-cday-section" />
                    <span class="sk-bone sk-cday-donut" />
                    <span class="sk-bone sk-cday-section" />
                </div>
            </div>
        </template>

        <template v-else-if="type === 'detail'">
            <div class="sk-detail">
                <div class="sk-detail-left">
                    <span class="sk-bone sk-line sk-line--lg" />
                    <div class="sk-detail-row sk-detail-row--4">
                        <span class="sk-bone sk-detail-box" v-for="n in 4" :key="'s' + n" />
                    </div>
                    <div class="sk-detail-row sk-detail-row--3">
                        <span class="sk-bone sk-detail-box sk-detail-box--tall" v-for="n in 3" :key="'m' + n" />
                    </div>
                    <div class="sk-bone sk-detail-donut" />
                </div>
                <div class="sk-detail-right">
                    <span class="sk-bone sk-detail-acc" v-for="n in 6" :key="'a' + n" />
                </div>
            </div>
        </template>

        <template v-else-if="type === 'dash-list'">
            <div v-for="n in items" :key="n" class="sk-dl-row">
                <span class="sk-bone sk-dl-avatar" />
                <div class="sk-dl-lines">
                    <span class="sk-bone sk-line sk-line--lg" />
                    <span class="sk-bone sk-line sk-line--sm" />
                </div>
            </div>
        </template>

        <template v-else-if="type === 'bar-chart'">
            <div class="sk-bc">
                <span v-for="(h, i) in barHeights" :key="i" class="sk-bone sk-bc-bar" :style="{ height: h + '%' }" />
            </div>
        </template>

        <template v-else-if="type === 'dashboard'">
            <div class="sk-dash">
                <div class="sk-dash-row">
                    <div class="sk-dash-main">
                        <div class="sk-dash-head">
                            <span class="sk-bone sk-dash-title" />
                            <span class="sk-bone sk-dash-sub" />
                        </div>
                        <div class="sk-dash-circles">
                            <span class="sk-bone sk-circle" v-for="n in 5" :key="'dc' + n" />
                        </div>
                        <div class="sk-dash-legend">
                            <span class="sk-bone sk-dash-chip" v-for="n in 3" :key="'dl' + n" />
                        </div>
                    </div>
                    <div class="sk-dash-side">
                        <div class="sk-dash-side-head"><span class="sk-bone sk-dash-sub" /></div>
                        <div class="sk-dl-row" v-for="n in 3" :key="'ds1' + n">
                            <span class="sk-bone sk-dl-avatar" />
                            <div class="sk-dl-lines">
                                <span class="sk-bone sk-line sk-line--lg" />
                                <span class="sk-bone sk-line sk-line--sm" />
                            </div>
                        </div>
                    </div>
                </div>
                <div class="sk-dash-row">
                    <div class="sk-dash-main">
                        <div class="sk-dash-head">
                            <span class="sk-bone sk-dash-title" />
                            <span class="sk-bone sk-dash-sub" />
                        </div>
                        <div class="sk-bc">
                            <span class="sk-bone sk-bc-bar" v-for="(h, i) in barHeights" :key="'db' + i"
                                :style="{ height: h + '%' }" />
                        </div>
                    </div>
                    <div class="sk-dash-side">
                        <div class="sk-dash-side-head"><span class="sk-bone sk-dash-sub" /></div>
                        <div class="sk-dl-row" v-for="n in 3" :key="'ds2' + n">
                            <span class="sk-bone sk-dl-avatar" />
                            <div class="sk-dl-lines">
                                <span class="sk-bone sk-line sk-line--lg" />
                                <span class="sk-bone sk-line sk-line--sm" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <template v-else-if="type === 'student-home'">
            <div class="sk-sh">
                <div class="sk-sh-card">
                    <div class="sk-sh-dots">
                        <span class="sk-bone sk-sh-dot" v-for="n in 7" :key="'shd' + n" />
                    </div>
                    <span class="sk-bone sk-sh-bar" />
                </div>
                <div class="sk-sh-stats">
                    <span class="sk-bone sk-sh-stat" v-for="n in 3" :key="'shs' + n" />
                </div>
                <span class="sk-bone sk-sh-workout" />
            </div>
        </template>

        <template v-else-if="type === 'student-routines'">
            <div class="sk-sr">
                <div class="sk-sr-left">
                    <div class="sk-sr-cal">
                        <span class="sk-bone sk-sr-cal-title" />
                        <div class="sk-sr-cal-grid">
                            <span class="sk-bone sk-sr-cal-cell" v-for="n in 28" :key="'src' + n" />
                        </div>
                    </div>
                    <div class="sk-sr-list">
                        <div class="sk-sr-item" v-for="n in 4" :key="'sri' + n">
                            <span class="sk-bone sk-sr-dot" />
                            <div class="sk-sr-item-lines">
                                <span class="sk-bone sk-line sk-line--lg" />
                                <span class="sk-bone sk-line sk-line--sm" />
                            </div>
                            <span class="sk-bone sk-sr-chip" />
                        </div>
                    </div>
                </div>
                <div class="sk-sr-right">
                    <div class="sk-sr-stats">
                        <span class="sk-bone sk-sr-stat" v-for="n in 3" :key="'srs' + n" />
                    </div>
                    <span class="sk-bone sk-sr-next" />
                </div>
            </div>
        </template>

        <template v-else-if="type === 'student-profile'">
            <div class="sk-sp">
                <div class="sk-sp-col">
                    <span class="sk-bone sk-sp-photo" />
                    <span class="sk-bone sk-sp-stat" v-for="n in 3" :key="'sps' + n" />
                </div>
                <div class="sk-sp-col">
                    <span class="sk-bone sk-sp-acc" />
                    <span class="sk-bone sk-sp-well" />
                </div>
                <div class="sk-sp-col">
                    <span class="sk-bone sk-sp-msg" />
                    <span class="sk-bone sk-sp-cal" />
                    <span class="sk-bone sk-sp-danger" />
                </div>
            </div>
        </template>

        <template v-else-if="type === 'coach-list'">
            <div class="sk-cl">
                <div class="sk-cl-item" v-for="n in items" :key="'cl' + n">
                    <span class="sk-bone sk-cl-av" />
                    <div class="sk-cl-lines">
                        <span class="sk-bone sk-line sk-line--lg" />
                        <span class="sk-bone sk-line sk-line--md" />
                        <span class="sk-bone sk-line sk-line--sm" />
                    </div>
                </div>
            </div>
        </template>

        <template v-else-if="type === 'book-shift'">
            <div class="sk-bs">
                <span class="sk-bone sk-line sk-line--lg" />
                <div class="sk-bs-days">
                    <span class="sk-bone sk-bs-day" v-for="n in 7" :key="'bsd' + n" />
                </div>
                <div class="sk-bs-list">
                    <div class="sk-bs-row" v-for="n in items" :key="'bsr' + n">
                        <span class="sk-bone sk-bs-dot" />
                        <div class="sk-bs-info">
                            <span class="sk-bone sk-line sk-line--md" />
                            <span class="sk-bone sk-line sk-line--sm" />
                            <span class="sk-bone sk-line sk-line--lg" />
                        </div>
                        <span class="sk-bone sk-bs-btn" />
                    </div>
                </div>
            </div>
        </template>

        <template v-else-if="type === 'coach-tabs'">
            <div class="sk-ct">
                <span class="sk-bone sk-ct-tab" v-for="n in items" :key="'ct' + n" />
            </div>
        </template>

        <template v-else-if="type === 'coach-cards'">
            <div class="sk-cc">
                <div class="sk-cc-card" v-for="n in items" :key="'cc' + n">
                    <span class="sk-bone sk-cc-img" />
                    <div class="sk-cc-body">
                        <span class="sk-bone sk-line sk-line--lg" />
                        <span class="sk-bone sk-line sk-line--sm" />
                    </div>
                </div>
            </div>
        </template>

        <template v-else-if="type === 'company-coach-profile'">
            <div class="sk-ccp">
                <div class="sk-ccp-col">
                    <span class="sk-bone sk-ccp-photo" />
                    <span class="sk-bone sk-ccp-stats" />
                </div>
                <div class="sk-ccp-col">
                    <span class="sk-bone sk-ccp-acc" />
                    <span class="sk-bone sk-ccp-btn" />
                </div>
            </div>
        </template>

        <template v-else-if="type === 'coach-profile'">
            <div class="sk-cp">
                <span class="sk-bone sk-cp-photo" />
                <span class="sk-bone sk-cp-name" />
                <span class="sk-bone sk-cp-bio" />
                <span class="sk-bone sk-cp-acc" v-for="n in 2" :key="'cpa' + n" />
            </div>
        </template>

        <template v-else-if="type === 'exercise-editor'">
            <div class="sk-ee">
                <div class="sk-ee-rail">
                    <span class="sk-bone sk-ee-rail-icon" v-for="n in 6" :key="'eer' + n" />
                </div>
                <div class="sk-ee-main">
                    <div class="sk-ee-top">
                        <div class="sk-ee-fields">
                            <div class="sk-ee-row" v-for="n in 2" :key="'eef' + n">
                                <span class="sk-bone sk-ee-field" />
                                <span class="sk-bone sk-ee-field sk-ee-field--xs" />
                                <span class="sk-bone sk-ee-field sk-ee-field--xs" />
                            </div>
                        </div>
                        <span class="sk-bone sk-ee-right" />
                    </div>
                    <span class="sk-bone sk-ee-config" />
                    <span class="sk-bone sk-ee-comment" />
                </div>
            </div>
        </template>

        <template v-else-if="type === 'auth-action'">
            <div class="sk-aa">
                <span class="sk-bone sk-aa-badge" />
                <span class="sk-bone sk-aa-title" />
                <span class="sk-bone sk-aa-line" />
                <span class="sk-bone sk-aa-line sk-aa-line--short" />
                <span class="sk-bone sk-aa-btn" />
            </div>
        </template>

        <template v-else-if="type === 'wellness'">
            <div class="sk-wl">
                <div class="sk-wl-dots">
                    <span class="sk-bone sk-wl-dot" v-for="n in 5" :key="'wld' + n" />
                </div>
                <span class="sk-bone sk-wl-step" />
                <span class="sk-bone sk-wl-question" />
                <span class="sk-bone sk-wl-control" />
            </div>
        </template>

        <template v-else-if="type === 'shifts'">
            <div class="sk-sh-toolbar">
                <span class="sk-bone sk-sh-chip" v-for="n in 4" :key="'shc' + n" />
                <span class="sk-bone sk-sh-btn" />
            </div>
            <div class="sk-sh-bar">
                <span class="sk-bone sk-sh-week" />
                <span class="sk-bone sk-sh-actions" />
            </div>
            <div class="sk-sh-grid">
                <span class="sk-bone sk-sh-head" v-for="n in 8" :key="'shh' + n" />
                <div class="sk-sh-body" v-for="n in 8" :key="'shb' + n">
                    <template v-if="n > 1">
                        <span class="sk-bone sk-sh-event" v-for="i in 2" :key="'she' + n + i"
                            :style="eventStyle(n, i)" />
                    </template>
                </div>
            </div>
        </template>

        <template v-else-if="type === 'cash'">
            <div class="sk-cash-kpis">
                <span class="sk-bone sk-cash-kpi" v-for="n in 4" :key="'ck' + n" />
            </div>
            <div class="sk-cash-cols">
                <div class="sk-cash-col" v-for="n in 2" :key="'cc' + n">
                    <span class="sk-bone sk-cash-title" />
                    <span class="sk-bone sk-cash-row" v-for="i in 5" :key="'cr' + n + i" />
                </div>
            </div>
        </template>

        <template v-else-if="type === 'stats-panel'">
            <div class="sk-sp-kpis">
                <span class="sk-bone sk-sp-kpi" v-for="n in items" :key="'spk' + n" />
            </div>
            <div class="sk-sp-charts">
                <span class="sk-bone sk-sp-chart" v-for="n in 4" :key="'spc' + n" />
            </div>
        </template>

        <div class="skeleton-loader__sheen" />
    </div>
</template>

<script>
const DEFAULT_COUNT = {
    'agenda-cards': 3,
    'week-dots': 7,
    'exercise-history': 4,
    'chat': 6,
    'chat-list': 6,
    'rows': 6,
    'lib-rows': 6,
    'circles': 5,
    'team-bars': 6,
    'team-cards': 5,
    'stat-grid': 6,
    'stat-table': 6,
    'table': 8,
    'calendar': 35,
    'dash-list': 3,
    'bar-chart': 6,
    'coach-list': 6,
    'coach-cards': 8,
    'coach-tabs': 5,
    'book-shift': 5,
    'stats-panel': 4,
}

const SHIFT_EVENT_TOPS = [14, 96, 40, 150, 70, 24, 120, 60]

const SHIFT_EVENT_HEIGHTS = [52, 40, 68, 46, 58, 74, 44, 62]

export default {
    name: "SkeletonLoader",
    props: {
        type: {
            type: String,
            required: true,
        },
        count: {
            type: Number,
            default: 0,
        },
    },
    data() {
        return {
            bubbleWidths: ['58%', '42%', '66%', '48%', '62%', '38%'],
            barHeights: [55, 80, 45, 90, 62, 72],
        }
    },
    computed: {
        items(vm = this) {
            return vm.count || DEFAULT_COUNT[vm.type] || 1
        },
    },
    methods: {
        eventStyle(column, index) {
            const position = (column * 2 + index) % SHIFT_EVENT_TOPS.length
            return {
                top: `${SHIFT_EVENT_TOPS[position]}px`,
                height: `${SHIFT_EVENT_HEIGHTS[position]}px`,
            }
        },
    },
}
</script>

<style scoped>
.skeleton-loader {
    position: relative;
    overflow: hidden;
}

.sk-bone {
    background: var(--v-surface2-base);
    border-radius: 3px;
}

.skeleton-loader__sheen {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 60%;
    background: linear-gradient(90deg,
        transparent 0%,
        var(--v-outline2-base) 50%,
        transparent 100%);
    opacity: 0.6;
    mix-blend-mode: screen;
    pointer-events: none;
    animation: skeleton-loader-sheen 1.4s ease-in-out infinite;
}

@keyframes skeleton-loader-sheen {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(266%); }
}

.sk-card {
    display: flex;
    align-items: stretch;
    height: 72px;
    border: 1px solid var(--v-outline-base);
    border-radius: 8px;
    overflow: hidden;
    margin-bottom: 8px;
}

.sk-card__thumb {
    width: 52px;
    flex-shrink: 0;
    border-radius: 0;
}

.sk-card__lines {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 7px;
    padding: 8px 12px;
}

.sk-line {
    height: 9px;
}

.sk-line--lg { width: 70%; }
.sk-line--md { width: 50%; }
.sk-line--sm { width: 40%; }

.skeleton-loader--week-dots {
    display: flex;
    gap: 4px;
    align-items: center;
}

.sk-day {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
}

.sk-day__label {
    width: 16px;
    height: 9px;
}

.sk-day__dot {
    width: 26px;
    height: 26px;
    border-radius: 50%;
}

.skeleton-loader--exercise-history {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.sk-hist-row {
    display: flex;
    align-items: stretch;
    height: 60px;
    border: 1px solid var(--v-outline-base);
    border-radius: 12px;
    overflow: hidden;
}

.sk-hist-date {
    width: 92px;
    flex-shrink: 0;
    border-radius: 0;
}

.sk-hist-data {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 7px;
    padding: 10px 12px;
}

.skeleton-loader--chat-list {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(300px, 100%), 1fr));
    gap: 12px;
}

.sk-chat-card {
    display: flex;
    align-items: stretch;
    height: 72px;
    border: 1px solid var(--v-outline-base);
    border-radius: 10px;
    overflow: hidden;
}

.sk-chat-thumb {
    width: 72px;
    flex-shrink: 0;
    border-radius: 0;
}

.sk-chat-lines {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 7px;
    padding: 12px;
}

.skeleton-loader--chat {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.sk-msg {
    display: flex;
}

.sk-msg--me {
    justify-content: flex-end;
}

.sk-bubble {
    height: 40px;
    border-radius: 12px;
}

.skeleton-loader--circles {
    display: flex;
    gap: 10px;
    justify-content: center;
}

.sk-circle {
    width: 48px;
    height: 48px;
    border-radius: 50%;
}

.skeleton-loader--team-bars {
    display: flex;
    flex-direction: column;
    gap: 7px;
}

.sk-tb-row {
    display: flex;
    align-items: center;
    gap: 8px;
}

.sk-tb-name {
    flex: 0 0 68px;
    height: 8px;
    border-radius: 4px;
}

.sk-tb-bar {
    flex: 1;
    height: 15px;
    border-radius: 4px;
}

.skeleton-loader--team-cards {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.sk-tc-card {
    display: flex;
    align-items: center;
    gap: 12px;
    height: 52px;
    padding: 0 16px;
    border: 1px solid var(--v-outline-base);
    border-radius: 14px;
    background: var(--v-surface-base);
}

.sk-tc-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex-shrink: 0;
}

.sk-tc-name {
    width: 140px;
    height: 13px;
    border-radius: 6px;
}

.sk-tc-meta {
    width: 130px;
    height: 11px;
    border-radius: 6px;
    margin-left: auto;
}

.sk-tc-chev {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    flex-shrink: 0;
}

.skeleton-loader--stat-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 14px;
}

.sk-sg-card {
    height: 320px;
    border-radius: 12px;
}

@media (max-width: 1100px) {
    .skeleton-loader--stat-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 700px) {
    .skeleton-loader--stat-grid {
        grid-template-columns: 1fr;
    }
}

.skeleton-loader--stat-table {
    border: 1px solid var(--v-outline-base);
    border-radius: 12px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.sk-stt-head {
    height: 42px;
    border-radius: 0;
}

.sk-stt-row {
    height: 46px;
    border-radius: 0;
    border-top: 1px solid var(--v-bg-base);
}

.skeleton-loader--eval-stats {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.sk-es-top {
    display: flex;
    gap: 16px;
}

.sk-es-card {
    width: 240px;
    height: 260px;
    border-radius: 14px;
    flex-shrink: 0;
}

.sk-es-table {
    flex: 1;
    height: 260px;
    border-radius: 14px;
}

.sk-es-charts {
    display: grid;
    grid-template-columns: 1.35fr 1fr 0.85fr;
    gap: 14px;
}

.sk-es-chart {
    height: 290px;
    border-radius: 14px;
}

@media (max-width: 1100px) {
    .sk-es-charts {
        grid-template-columns: 1fr 1fr;
    }
}

@media (max-width: 860px) {
    .sk-es-top {
        flex-direction: column;
    }

    .sk-es-card {
        width: 100%;
        height: 180px;
    }
}

@media (max-width: 700px) {
    .sk-es-charts {
        grid-template-columns: 1fr;
    }
}

.sk-tbl-head {
    height: 40px;
    background: linear-gradient(180deg, var(--v-surface2-base) 0%, var(--v-surface-base) 100%);
    border-bottom: 1px solid var(--v-outline-base);
}

.sk-tbl-row {
    display: flex;
    align-items: center;
    gap: 10px;
    height: 60px;
    padding: 0 16px;
    border-bottom: 1px solid var(--v-outline-base);
}

.sk-tbl-row:last-child {
    border-bottom: none;
}

.sk-tbl-avatar {
    width: 34px;
    height: 34px;
    border-radius: 8px;
    flex-shrink: 0;
}

.sk-tbl-lines {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 7px;
}

.sk-tbl-cell {
    width: 90px;
    height: 11px;
    border-radius: 4px;
    flex-shrink: 0;
}

.sk-tbl-cell--sm {
    width: 60px;
}

.skeleton-loader--rows {
    display: flex;
    flex-direction: column;
}

.sk-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 11px 0;
    border-bottom: 1px solid var(--v-outline-base);
}

.sk-row:last-child {
    border-bottom: none;
}

.sk-row .sk-line {
    flex: 1;
    height: 11px;
}

.sk-row__ctrl {
    width: 40px;
    height: 22px;
    border-radius: 11px;
    flex-shrink: 0;
}

.skeleton-loader--lib-rows {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.sk-lib-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    border: 1px solid var(--v-outline-base);
    border-radius: 10px;
}

.sk-lib-icon {
    width: 42px;
    height: 42px;
    border-radius: 8px;
    flex-shrink: 0;
}

.sk-lib-lines {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 7px;
}

.sk-lib-more {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    flex-shrink: 0;
}

.skeleton-loader--zapping-select {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.sk-zap-desc {
    height: 11px;
    width: 100%;
    align-self: center;
}

.sk-zap-desc--short {
    width: 60%;
}

.sk-zap-tabs {
    display: flex;
    gap: 8px;
}

.sk-zap-tab {
    flex: 1;
    height: 38px;
    border-radius: 8px;
}

.sk-zap-field {
    width: 100%;
    height: 44px;
    border-radius: 8px;
}

.sk-athlete {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    border: 1px solid var(--v-outline-base);
    border-radius: 12px;
}

.sk-athlete-logo {
    width: 40px;
    height: 40px;
    border-radius: 9px;
    flex-shrink: 0;
}

.sk-athlete-name {
    height: 13px;
    width: 55%;
    border-radius: 4px;
}

.sk-ae {
    max-width: 720px;
    margin: 0 auto;
    padding: 16px;
    border: 1px solid var(--v-outline-base);
    border-radius: 12px;
}

.sk-ae-steps {
    display: flex;
    justify-content: center;
    gap: 60px;
    padding-bottom: 16px;
    border-bottom: 1px solid var(--v-outline-base);
}

.sk-ae-step {
    width: 28px;
    height: 28px;
    border-radius: 50%;
}

.sk-ae-body {
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 20px 0;
}

.sk-ae-field {
    height: 42px;
    border-radius: 8px;
}

.sk-ae-img {
    height: 100px;
    border-radius: 8px;
}

.sk-ae-actions {
    display: flex;
    gap: 8px;
    padding-top: 16px;
    border-top: 1px solid var(--v-outline-base);
}

.sk-ae-btn {
    flex: 1 1 0;
    height: 42px;
    border-radius: 8px;
}

.sk-ad {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
}

.sk-ad-left {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.sk-ad-right {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.sk-ad-hero {
    height: 220px;
    border-radius: 14px;
}

.sk-ad-stats {
    display: flex;
    gap: 8px;
}

.sk-ad-stat {
    flex: 1;
    height: 64px;
    border-radius: 14px;
}

.sk-ad-block {
    height: 56px;
    border-radius: 12px;
}

@media (min-width: 960px) {
    .sk-ad {
        grid-template-columns: 1fr 1fr;
        gap: 24px;
        align-items: start;
    }
}

.sk-cal-head {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    padding: 10px 8px;
    border-bottom: 1px solid var(--v-outline-base);
}

.sk-cal-weekday {
    height: 9px;
    width: 55%;
    margin: 0 auto;
}

.sk-cal-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
}

.sk-cal-cell {
    min-height: 92px;
    border-right: 1px solid var(--v-outline2-base);
    border-bottom: 1px solid var(--v-outline2-base);
    padding: 8px 6px;
    display: flex;
    flex-direction: column;
    gap: 5px;
    overflow: hidden;
}

.sk-cal-cell:nth-child(7n) {
    border-right: none;
}

.sk-cal-date {
    width: 16px;
    height: 12px;
    margin-bottom: 2px;
}

.sk-cal-ev {
    height: 8px;
    border-radius: 3px;
}

.sk-week {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    min-height: calc(100vh - 250px);
}

.sk-week-col {
    display: flex;
    flex-direction: column;
    border-right: 1px solid var(--v-outline2-base);
}

.sk-week-col:last-child {
    border-right: none;
}

.sk-week-head {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 10px 8px;
    background: var(--v-surface-base);
    border-bottom: 1px solid var(--v-outline-base);
}

.sk-week-weekday {
    width: 40%;
    height: 9px;
}

.sk-week-date {
    width: 18px;
    height: 12px;
    align-self: flex-start;
}

.sk-week-body {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 8px 6px;
}

.sk-week-ev {
    height: 22px;
    border-radius: 4px;
}

.sk-cday {
    display: flex;
    height: calc(100vh - 260px);
    min-height: 0;
    border-top: 1px solid var(--v-outline-base);
}

.sk-cday-col {
    display: flex;
    flex-direction: column;
    padding: 12px 10px;
    min-width: 0;
    overflow: hidden;
}

.sk-cday-col--left {
    width: clamp(220px, 30%, 420px);
    border-right: 1px solid var(--v-outline-base);
}

.sk-cday-col--mid {
    flex: 1;
    background: var(--v-bg-base);
    border-right: 1px solid var(--v-outline-base);
}

.sk-cday-col--right {
    width: clamp(240px, 25%, 320px);
    padding: 0 0 12px;
}

.sk-cday-block {
    height: 64px;
    border-radius: 12px;
    margin-bottom: 10px;
}

.sk-cday-save {
    margin-top: auto;
    height: 38px;
    border-radius: 9px;
}

.sk-cday-header {
    height: 34px;
    border-radius: 8px;
    margin-bottom: 12px;
}

.sk-cday-hist {
    height: 96px;
    border-radius: 12px;
    margin-bottom: 12px;
}

.sk-cday-tabs {
    display: flex;
    gap: 6px;
    margin-bottom: 12px;
}

.sk-cday-tab {
    flex: 1;
    height: 30px;
    border-radius: 7px;
}

.sk-cday-field {
    height: 40px;
    border-radius: 8px;
    margin-bottom: 12px;
}

.sk-cday-row {
    height: 52px;
    border-radius: 10px;
    margin-bottom: 8px;
}

.sk-cday-navbar {
    height: 41px;
    border-radius: 0;
    margin-bottom: 12px;
}

.sk-cday-metrics {
    display: flex;
    gap: 1px;
    padding: 0 12px;
    margin-bottom: 12px;
}

.sk-cday-metric {
    flex: 1;
    height: 52px;
    border-radius: 8px;
}

.sk-cday-section {
    height: 96px;
    border-radius: 10px;
    margin: 0 12px 12px;
}

.sk-cday-donut {
    height: 160px;
    border-radius: 10px;
    margin: 0 12px 12px;
}

.skeleton-loader--detail {
    padding: 16px;
}

.sk-detail {
    display: grid;
    grid-template-columns: 1fr 420px;
    gap: 16px;
}

.sk-detail-left {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.sk-detail-row {
    display: grid;
    gap: 8px;
}

.sk-detail-row--4 {
    grid-template-columns: repeat(4, 1fr);
}

.sk-detail-row--3 {
    grid-template-columns: repeat(3, 1fr);
}

.sk-detail-box {
    height: 56px;
    border-radius: 10px;
}

.sk-detail-box--tall {
    height: 72px;
}

.sk-detail-donut {
    height: 200px;
    border-radius: 10px;
}

.sk-detail-right {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.sk-detail-acc {
    height: 50px;
    border-radius: 10px;
}

@media (max-width: 960px) {
    .sk-detail {
        grid-template-columns: 1fr;
    }
}

.skeleton-loader--dash-list {
    display: flex;
    flex-direction: column;
}

.sk-dl-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 16px;
    border-bottom: 1px solid var(--v-outline-base);
}

.sk-dl-row:last-child {
    border-bottom: none;
}

.sk-dl-avatar {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    flex-shrink: 0;
}

.sk-dl-lines {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.skeleton-loader--bar-chart {
    height: 100%;
}

.sk-bc {
    display: flex;
    align-items: flex-end;
    justify-content: space-around;
    gap: 8px;
    height: 100%;
    padding: 12px 6px 26px;
}

.sk-bc-bar {
    flex: 1;
    max-width: 46px;
    border-radius: 6px 6px 0 0;
}

.sk-dash {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.sk-dash-row {
    display: grid;
    grid-template-columns: 1fr 240px;
    gap: 8px;
}

.sk-dash-main,
.sk-dash-side {
    border: 1px solid var(--v-outline-base);
    border-radius: 14px;
    background: var(--v-surface-base);
    overflow: hidden;
}

.sk-dash-main {
    padding: 14px 18px;
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.sk-dash-head {
    display: flex;
    flex-direction: column;
    gap: 7px;
}

.sk-dash-title {
    width: 40%;
    height: 14px;
    border-radius: 5px;
}

.sk-dash-sub {
    width: 60%;
    height: 10px;
    border-radius: 5px;
}

.sk-dash-circles {
    display: flex;
    justify-content: space-around;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    padding: 8px 0;
}

.sk-dash-legend {
    display: flex;
    gap: 14px;
    border-top: 1px solid var(--v-outline-base);
    padding-top: 10px;
    margin-top: auto;
}

.sk-dash-chip {
    width: 60px;
    height: 10px;
    border-radius: 5px;
}

.sk-dash-main .sk-bc {
    height: 210px;
}

.sk-dash-side-head {
    padding: 12px 16px;
    border-bottom: 1px solid var(--v-outline-base);
}

.sk-dash-side-head .sk-dash-sub {
    width: 55%;
}

@media (max-width: 960px) {
    .sk-dash-row {
        grid-template-columns: 1fr;
    }
}

.sk-sh {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.sk-sh-card {
    border: 1px solid var(--v-outline-base);
    border-radius: 14px;
    padding: 16px 18px;
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.sk-sh-dots {
    display: flex;
    justify-content: space-between;
}

.sk-sh-dot {
    width: 34px;
    height: 34px;
    border-radius: 50%;
}

.sk-sh-bar {
    height: 6px;
    border-radius: 3px;
}

.sk-sh-stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
}

.sk-sh-stat {
    height: 90px;
    border-radius: 12px;
}

.sk-sh-workout {
    height: 220px;
    border-radius: 14px;
}

@media (max-width: 599px) {
    .sk-sh-dot {
        width: 26px;
        height: 26px;
    }
}

.sk-sr {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 20px;
    align-items: start;
}

.sk-sr-left,
.sk-sr-right {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.sk-sr-cal {
    border: 1px solid var(--v-outline-base);
    border-radius: 14px;
    padding: 16px 18px;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.sk-sr-cal-title {
    height: 16px;
    width: 40%;
    border-radius: 6px;
    align-self: center;
}

.sk-sr-cal-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 6px;
}

.sk-sr-cal-cell {
    aspect-ratio: 1;
    border-radius: 8px;
}

.sk-sr-list {
    border: 1px solid var(--v-outline-base);
    border-radius: 14px;
    padding: 8px 14px;
    display: flex;
    flex-direction: column;
}

.sk-sr-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 0;
    border-bottom: 1px solid var(--v-outline-base);
}

.sk-sr-item:last-child {
    border-bottom: none;
}

.sk-sr-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex-shrink: 0;
}

.sk-sr-item-lines {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.sk-sr-chip {
    width: 72px;
    height: 22px;
    border-radius: 11px;
    flex-shrink: 0;
}

.sk-sr-stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
}

.sk-sr-stat {
    height: 90px;
    border-radius: 12px;
}

.sk-sr-next {
    height: 200px;
    border-radius: 14px;
}

@media (max-width: 959px) {
    .sk-sr {
        grid-template-columns: 1fr;
    }
}

.sk-sp {
    display: grid;
    grid-template-columns: 260px 1fr 280px;
    gap: 20px;
    align-items: start;
}

.sk-sp-col {
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-width: 0;
}

.sk-sp-photo {
    width: 100%;
    aspect-ratio: 1;
    border-radius: 14px;
}

.sk-sp-stat {
    height: 58px;
    border-radius: 10px;
}

.sk-sp-acc {
    height: 460px;
    border-radius: 14px;
}

.sk-sp-well {
    height: 320px;
    border-radius: 14px;
}

.sk-sp-msg {
    height: 96px;
    border-radius: 14px;
}

.sk-sp-cal {
    height: 300px;
    border-radius: 14px;
}

.sk-sp-danger {
    height: 44px;
    border-radius: 10px;
}

@media (max-width: 1263px) {
    .sk-sp {
        grid-template-columns: 220px 1fr 240px;
        gap: 14px;
    }
}

@media (max-width: 959px) {
    .sk-sp {
        grid-template-columns: 1fr;
        gap: 14px;
    }
}

.sk-cl {
    display: flex;
    flex-direction: column;
}

.sk-cl-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 0;
    border-bottom: 1px solid var(--v-outline-base);
}

.sk-cl-av {
    width: 56px;
    height: 56px;
    border-radius: 10px;
    flex-shrink: 0;
}

.sk-cl-lines {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 7px;
}

.sk-bs {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.sk-bs-days {
    display: flex;
    gap: 6px;
}

.sk-bs-day {
    flex: 1 1 0;
    min-width: 0;
    max-width: 62px;
    height: 48px;
    border-radius: 12px;
}

.sk-bs-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.sk-bs-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    border: 1px solid var(--v-outline-base);
    border-radius: 14px;
}

.sk-bs-dot {
    width: 10px;
    height: 10px;
    flex-shrink: 0;
    border-radius: 50%;
}

.sk-bs-info {
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1 1 auto;
    min-width: 0;
}

.sk-bs-btn {
    width: 82px;
    height: 28px;
    flex-shrink: 0;
    border-radius: 6px;
}

.sk-ct {
    display: flex;
    gap: 4px;
    padding: 3px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 10px;
    overflow: hidden;
}

.sk-ct-tab {
    height: 34px;
    flex: 1;
    min-width: 90px;
    border-radius: 7px;
}

.sk-cc {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 12px;
}

.sk-cc-card {
    border: 1px solid var(--v-outline-base);
    border-radius: 12px;
    overflow: hidden;
}

.sk-cc-img {
    display: block;
    width: 100%;
    height: 80px;
    border-radius: 0;
}

.sk-cc-body {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 14px 12px;
}

.sk-ccp {
    display: grid;
    grid-template-columns: 260px 1fr;
    gap: 20px;
    align-items: start;
}

.sk-ccp-col {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.sk-ccp-photo {
    width: 100%;
    aspect-ratio: 1;
    border-radius: 14px;
}

.sk-ccp-stats {
    width: 100%;
    height: 164px;
    border-radius: 14px;
}

.sk-ccp-acc {
    width: 100%;
    height: 116px;
    border-radius: 14px;
}

.sk-ccp-btn {
    width: 200px;
    height: 36px;
    border-radius: 8px;
    align-self: flex-end;
}

@media (max-width: 960px) {
    .sk-ccp {
        grid-template-columns: 1fr;
    }
}

.sk-cp {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
}

.sk-cp-photo {
    width: 140px;
    height: 140px;
    border-radius: 12px;
}

.sk-cp-name {
    width: 220px;
    height: 20px;
    border-radius: 6px;
}

.sk-cp-bio {
    width: 280px;
    max-width: 100%;
    height: 12px;
    border-radius: 6px;
}

.sk-cp-acc {
    width: 100%;
    height: 52px;
    border-radius: 10px;
    margin-top: 8px;
}

.skeleton-loader--exercise-editor {
    flex: 1;
    min-width: 0;
}

.sk-ee {
    display: flex;
    height: 100%;
    min-height: 0;
}

.sk-ee-rail {
    width: 56px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 14px 8px;
    background: var(--v-bg-base);
    border-right: 1px solid var(--v-outline-base);
}

.sk-ee-rail-icon {
    height: 24px;
    border-radius: 6px;
}

.sk-ee-main {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 16px 24px;
}

.sk-ee-top {
    display: flex;
    gap: 16px;
    align-items: flex-start;
}

.sk-ee-fields {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.sk-ee-row {
    display: flex;
    gap: 10px;
}

.sk-ee-field {
    flex: 1;
    height: 38px;
    border-radius: 8px;
}

.sk-ee-field--xs {
    flex: 0 0 74px;
}

.sk-ee-right {
    width: 224px;
    height: 128px;
    border-radius: 10px;
    flex-shrink: 0;
}

.sk-ee-config {
    height: 200px;
    border-radius: 10px;
}

.sk-ee-comment {
    height: 68px;
    border-radius: 8px;
}

.sk-aa {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    width: 100%;
    max-width: 380px;
    margin: 0 auto;
}

.sk-aa-badge {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    margin-bottom: 6px;
}

.sk-aa-title {
    width: 60%;
    height: 20px;
    border-radius: 6px;
}

.sk-aa-line {
    width: 100%;
    height: 11px;
    border-radius: 5px;
}

.sk-aa-line--short {
    width: 70%;
}

.sk-aa-btn {
    width: 100%;
    height: 44px;
    border-radius: 8px;
    margin-top: 10px;
}

.sk-wl {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    padding: 0 16px;
}

.sk-wl-dots {
    display: flex;
    gap: 6px;
    padding: 14px 0 4px;
}

.sk-wl-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
}

.sk-wl-step {
    width: 64px;
    height: 12px;
    border-radius: 6px;
}

.sk-wl-question {
    width: 80%;
    height: 24px;
    border-radius: 8px;
}

.sk-wl-control {
    width: 100%;
    height: 220px;
    border-radius: 12px;
    margin-top: 8px;
}

.skeleton-loader--shifts {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.sk-sh-toolbar {
    display: flex;
    align-items: center;
    gap: 6px;
}

.sk-sh-chip {
    width: 88px;
    height: 26px;
    border-radius: 20px;
}

.sk-sh-btn {
    width: 104px;
    height: 28px;
    border-radius: 8px;
    margin-left: auto;
}

.sk-sh-bar {
    display: flex;
    align-items: center;
}

.sk-sh-week {
    width: 190px;
    height: 28px;
    border-radius: 8px;
}

.sk-sh-actions {
    width: 210px;
    height: 28px;
    border-radius: 8px;
    margin-left: auto;
}

.sk-sh-grid {
    display: grid;
    grid-template-columns: 56px repeat(7, 1fr);
    gap: 4px;
    padding: 10px;
    border: 1px solid var(--v-outline-base);
    border-radius: 12px;
}

.sk-sh-head {
    height: 26px;
    border-radius: 6px;
}

.sk-sh-body {
    position: relative;
    height: 220px;
}

.sk-sh-event {
    position: absolute;
    left: 2px;
    right: 2px;
    border-radius: 6px;
}

.skeleton-loader--cash {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.sk-cash-kpis {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 10px;
}

.sk-cash-kpi {
    height: 86px;
    border-radius: 12px;
}

.sk-cash-cols {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
}

.sk-cash-col {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.sk-cash-title {
    height: 20px;
    width: 40%;
    border-radius: 6px;
}

.sk-cash-row {
    height: 40px;
    border-radius: 8px;
}

.skeleton-loader--stats-panel {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.sk-sp-kpis {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 10px;
}

.sk-sp-kpi {
    height: 86px;
    border-radius: 12px;
}

.sk-sp-charts {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
}

.sk-sp-chart {
    height: 240px;
    border-radius: 12px;
}

@media (max-width: 900px) {

    .sk-cash-kpis,
    .sk-sp-kpis {
        grid-template-columns: repeat(2, 1fr);
    }

    .sk-cash-cols,
    .sk-sp-charts {
        grid-template-columns: 1fr;
    }
}
</style>
